import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { map, pairwise, Subscription } from 'rxjs';
import { InsuranceFlowDetailsResponse } from 'src/app/_types/insurance-flow.types';
import { CreateLeadResponse } from 'src/app/_types/proposals.type';
import { getInsuranceFlowBackgroundColor } from 'src/app/enums/insurance-flow/insurance-flow-status.enum';
import { SlideInModalComponent } from 'src/app/shared/components/slide-in-modal/slide-in-modal.component';

import { TaskDetailsBodyComponent } from './components/task-details-body/task-details-body.component';
import { TaskDetailsHeaderActionsComponent } from './components/task-details-header-actions/task-details-header-actions.component';
import { TaskDetailsHeaderInfoComponent } from './components/task-details-header-info/task-details-header-info.component';
import { TaskDetailsService } from './task-details.service';

@Component({
  selector: 'app-task-details',
  standalone: true,
  imports: [
    SlideInModalComponent,
    TaskDetailsHeaderInfoComponent,
    TaskDetailsHeaderActionsComponent,
    TaskDetailsBodyComponent,
  ],
  templateUrl: './task-details.component.html',
  styleUrl: './task-details.component.css',
  // We want each instance of the component and its children to have its own mediator service
  providers: [TaskDetailsService],
})
export class TaskDetailsComponent implements OnInit, OnDestroy {
  id: string | null = null;

  private taskStatusSubscription?: Subscription;

  private changedSubscription?: Subscription;

  @Output() updated = new EventEmitter();

  /** Passed to the slide-in: raised when this modal opens above another one. */
  @Input() layer = 0;

  @ViewChild('modal') modal?: SlideInModalComponent;

  /** Set on the first « Voir l'affaire »; that is what builds the stacked modal. */
  protected stackedFlowId: string | null = null;

  @ViewChild('stackedModal') private stackedModal?: TaskDetailsComponent;

  constructor(
    private readonly taskDetailsService: TaskDetailsService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // emits updated only when the status changes
    this.taskStatusSubscription = this.taskDetailsService.state$
      .pipe(
        map((state) => ({
          id: state.insuranceFlowData?.id,
          status: state.insuranceFlowData?.status,
        })),
        pairwise()
      )
      .subscribe(([previousTask, currentTask]) => {
        if (
          previousTask.id &&
          previousTask.id === currentTask.id &&
          previousTask.status &&
          currentTask.status &&
          previousTask.status !== currentTask.status
        ) {
          this.updated.emit();
        }
      });
    // A change of its own the affaire's status does not carry: a cancellation
    // opened on its policy leaves the policy exactly where it was.
    this.changedSubscription = this.taskDetailsService.changed$.subscribe(() =>
      this.updated.emit()
    );
  }

  ngOnDestroy(): void {
    this.taskStatusSubscription?.unsubscribe();
    this.changedSubscription?.unsubscribe();
  }

  openModal(id: string): void {
    this.id = id;
    this.taskDetailsService.loadTaskDetails(id);
    if (this.modal) {
      this.modal.openModal();
    }
  }

  /** Closes whatever is stacked above too, at any depth. */
  closeModal(): void {
    this.stackedModal?.closeModal();
    this.modal?.closeModal(false);
  }

  /** Another flow's modal above this one — the replacement of a cancellation, say. */
  openStackedFlow(flowId: string): void {
    this.stackedFlowId = flowId;
    // Renders it now on a first click, so it can open at once.
    this.changeDetectorRef.detectChanges();
    this.stackedModal?.openModal(flowId);
  }

  refreshTaskDetails(emit = false): void {
    this.taskDetailsService.refreshTaskDetails();
    if (emit) this.updated.emit();
  }

  getInsuranceFlowBackgroundColor = getInsuranceFlowBackgroundColor;

  protected get insuranceFlowData(): InsuranceFlowDetailsResponse | null {
    return this.taskDetailsService.state.insuranceFlowData;
  }

  onLeadCreated({ flowId }: CreateLeadResponse): void {
    this.openModal(flowId);
    this.updated.emit();
  }
}
